/*
 * /bruxism-monitor — ночной поиск скрежета зубами.
 *
 * Спрос («бруксизм» 49 974 + «скрежет зубами во сне» 3 780 + «капа от
 * бруксизма» 4 769) закрыт нативными приложениями (Sleep Cycle, SleepScore) и
 * статьями. Браузерного инструмента нет вовсе.
 *
 * Устройство отличается от приложений одним решением: тул НЕ выносит
 * вердикт. Он находит кандидатов и КЛАДЁТ РЯДОМ ЗАПИСЬ. Утром человек
 * слушает и отвечает сам — потому что никакой классификатор не отличит
 * скрежет от трения простыни надёжно, а «вы скрипели 40 раз» без
 * доказательства отправляет покупать капу по ошибке прибора.
 *
 * И вторая честность, которой нет ни у кого: СЖАТИЕ ЧЕЛЮСТЕЙ БЕЗЗВУЧНО.
 * Это примерно половина ночного бруксизма, и микрофон её не видит в
 * принципе. Тул говорит это на первом экране, а не в мелком шрифте.
 */

import { GrindDetector, ClipRecorder } from './detector.js';

const CFG = window.__BRUXISM || {};
const T = CFG.i18n || {};
const $ = s => document.querySelector(s);
const $$ = s => Array.from(document.querySelectorAll(s));

import { loadNights, saveNight, clearNights, summarize } from './nights.js';

const KEY = 'bruxism-monitor:last';

const app = {
    stream: null,
    ctx: null,
    det: null,
    clipper: null,
    mic: null,
    baseline: null,
    startedAt: null,
    events: [],       // { id, at, duration, score, verdict: null|'yes'|'no' }
    clips: new Map(), // id → Blob
    timer: null,
};

// ── запуск ──────────────────────────────────────────────────────────────

function boot() {
    $('#bxStart').addEventListener('click', start);
    $('#bxStop').addEventListener('click', stop);
    $('#bxForget').addEventListener('click', forget);
    $('#bxCopy').addEventListener('click', copyReport);
    const nc = $('#bxNightsClear');
    if (nc) {
        nc.addEventListener('click', () => {
            if (!window.confirm(T.nights_clear_confirm)) return;
            clearNights();
            renderNights();
        });
    }
    renderNights();
    $('#bxSens').addEventListener('change', () => {
        window.__toolSettings && window.__toolSettings.save('bruxism-monitor', 'sens', $('#bxSens').value);
        if (app.det) app.det.sensitivity = sensDb();
    });

    const saved = window.__toolSettings && window.__toolSettings.load('bruxism-monitor', 'sens', 'normal');
    if (saved) $('#bxSens').value = saved;

    restoreLast();
    if (window.__stage) window.__stage('ready');
}

function sensDb() {
    return { high: 10, normal: 14, low: 19 }[$('#bxSens').value] || 14;
}

// ── ночь ────────────────────────────────────────────────────────────────

async function start() {
    $('#bxStart').disabled = true;
    setPhase('asking');

    /*
     * ⚠️ Через MicManager, а не голый getUserMedia. Первая версия этого файла
     * классифицировала отказ сама — и это ровно тот класс, который в проекте
     * уже решён один раз: примитив знает причины (denied / not_found / busy /
     * os_blocked / blocked_env), несёт переведённую фразу на каждую, умеет
     * показать инструкцию под конкретный браузер и повторить запрос, когда
     * человек выдал разрешение позже. Своя копия была беднее по всем пунктам.
     * Сторож — MicFailureIsExplainableTest.
     */
    app.mic = new MicManager({
        locale: window.__LOCALE,
        onRetry: () => { $('#bxStart').disabled = false; start(); },
    });

    try {
        app.stream = await app.mic.startRaw({
            audio: {
                echoCancellation: false,   // ⚠️ обработка съедает шипящие,
                noiseSuppression: false,   // а скрежет — это ровно они
                autoGainControl: false,    // и АРУ ломает пороги по уровню
            },
        });
    } catch (e) {
        $('#bxStart').disabled = false;
        setPhase('idle');
        // Фраза берётся у примитива: она переведена и названа по причине.
        const box = $('#bxMicErr');
        box.hidden = false;
        box.textContent = (e && e.message) || T.mic_generic;
        // Отказ микрофона не виден НИ ОДНОМУ счётчику: ночь просто не
        // начинается. Причина у примитива уже классифицирована — берём её.
        if (window.__aeDiagnostic) {
            window.__aeDiagnostic({ issue: 'mic_denied', code: (e && e.code) || 'generic' });
        }
        return;
    }

    app.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (window.__liveAudioCtxReady) {
        const live = await window.__liveAudioCtxReady(app.ctx);
        if (live && live !== app.ctx) app.ctx = live;
    }
    const source = app.ctx.createMediaStreamSource(app.stream);

    setPhase('calibrating');
    app.baseline = await calibrate(source);

    app.startedAt = Date.now();
    app.events = [];
    app.clips.clear();

    app.det = new GrindDetector(app.ctx, source, {
        baselineDb: app.baseline,
        sensitivity: sensDb(),
        onLevel: onLevel,
        onEvent: onEvent,
    });
    app.det.start();

    app.clipper = new ClipRecorder(app.stream, { seconds: 8 });
    $('#bxNoClips').hidden = app.clipper.enabled;

    // Экран гасить нельзя: на телефоне вкладка со спящим экраном может
    // потерять поток. Просим общий примитив, а не пишем свой.
    if (window.__wakeLock) window.__wakeLock.request().catch(() => {});

    setPhase('running');
    app.timer = setInterval(renderRunning, 1000);
    renderRunning();

    if (window.__aeFunnel) window.__aeFunnel('night_started', { baseline: Math.round(app.baseline) });
}

/** 6 секунд тишины комнаты → медианный уровень как базовая линия. */
function calibrate(source) {
    return new Promise(resolve => {
        const an = source.context.createAnalyser();
        an.fftSize = 2048;
        source.connect(an);
        const bins = new Float32Array(an.frequencyBinCount);
        const binHz = source.context.sampleRate / an.fftSize;
        const from = Math.floor(2000 / binHz), to = Math.ceil(8000 / binHz);
        const samples = [];
        const total = 6000;
        const t0 = performance.now();

        const step = () => {
            an.getFloatFrequencyData(bins);
            let sum = 0;
            for (let i = from; i <= to && i < bins.length; i++) sum += Math.pow(10, bins[i] / 10);
            samples.push(10 * Math.log10(sum / (to - from + 1) + 1e-12));

            const done = performance.now() - t0;
            $('#bxCalBar').style.width = Math.min(100, (done / total) * 100) + '%';

            if (done < total) { requestAnimationFrame(step); return; }

            samples.sort((a, b) => a - b);
            const median = samples[Math.floor(samples.length / 2)];
            try { an.disconnect(); } catch (_) {}
            // Слишком шумная комната — предупреждаем, но не запрещаем: спать
            // человек всё равно будет здесь.
            $('#bxNoisy').hidden = median < -55;
            resolve(median);
        };
        requestAnimationFrame(step);
    });
}

function onLevel(l) {
    const over = Math.max(0, Math.min(1, (l.high - app.baseline) / 40));
    $('#bxMeter').style.transform = `scaleX(${over.toFixed(3)})`;
}

function onEvent(e) {
    const id = 'e' + app.events.length + '-' + e.at;
    app.events.push({ id, at: e.at, duration: e.duration, score: e.score, verdict: null });

    // Клип берём СРАЗУ: буфер кольцевой, через полминуты этого звука в нём
    // уже не будет.
    if (app.clipper && app.clipper.enabled) {
        const blob = app.clipper.snapshot();
        if (blob) app.clips.set(id, blob);
    }

    persist();
    renderRunning();
}

async function stop() {
    if (app.timer) clearInterval(app.timer);
    app.timer = null;
    if (app.det) app.det.stop();
    if (app.clipper) app.clipper.stop();
    if (app.mic) app.mic.stop();
    else if (app.stream) app.stream.getTracks().forEach(t => t.stop());
    if (app.ctx) { try { await app.ctx.close(); } catch (_) {} }
    if (window.__wakeLock) window.__wakeLock.release();

    app.det = null; app.stream = null; app.ctx = null; app.mic = null;
    persist();
    setPhase('done');
    renderReport();

    if (window.__aeToolComplete) {
        window.__aeToolComplete({ ok: 1, events: app.events.length, minutes: Math.round(nightMinutes()) });
    }
}

function forget() {
    app.events = [];
    app.clips.clear();
    app.startedAt = null;
    try { localStorage.removeItem(KEY); } catch (_) {}
    setPhase('idle');
    $('#bxStart').disabled = false;
}

// ── состояние между запусками ───────────────────────────────────────────

/*
 * Метаданные переживают перезагрузку, ЗВУК — нет: клипы живут в памяти
 * вкладки. Это осознанный размен, а не недоделка: ночь звука — это десятки
 * мегабайт, которые пришлось бы писать в хранилище браузера у человека,
 * пришедшего послушать пять фрагментов. Строка на экране говорит об этом
 * прямо, чтобы утренняя пропажа кнопок «послушать» не выглядела поломкой.
 */
function persist() {
    try {
        localStorage.setItem(KEY, JSON.stringify({
            startedAt: app.startedAt,
            endedAt: Date.now(),
            baseline: app.baseline,
            events: app.events,
        }));
    } catch (_) {}
}

function restoreLast() {
    try {
        const raw = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (!raw || !raw.startedAt || !raw.events) return;
        app.startedAt = raw.startedAt;
        app.endedAt = raw.endedAt;
        app.baseline = raw.baseline;
        app.events = raw.events;
        setPhase('done');
        renderReport();
        $('#bxClipsGone').hidden = !app.events.length;
    } catch (_) {}
}

// ── отрисовка ───────────────────────────────────────────────────────────

function setPhase(p) {
    $$('[data-phase]').forEach(n => {
        n.hidden = !n.getAttribute('data-phase').split(' ').includes(p);
    });
}

function nightMinutes() {
    const end = app.endedAt || Date.now();
    return app.startedAt ? (end - app.startedAt) / 60000 : 0;
}

function renderRunning() {
    const mins = nightMinutes();
    $('#bxElapsed').textContent = fmtDur(mins);
    $('#bxCount').textContent = String(app.events.length);
    $('#bxRate').textContent = mins > 20
        ? fmt(T.per_hour, { n: (app.events.length / (mins / 60)).toFixed(1) })
        : T.rate_wait;
}

function renderReport() {
    const list = $('#bxList');
    list.textContent = '';

    const mins = nightMinutes();
    $('#bxRepTime').textContent = fmtDur(mins);
    $('#bxRepCount').textContent = String(app.events.length);
    const yes = app.events.filter(e => e.verdict === 'yes').length;
    const no = app.events.filter(e => e.verdict === 'no').length;
    $('#bxRepYes').textContent = String(yes);
    $('#bxConfirmed').hidden = !(yes || no);

    $('#bxNone').hidden = app.events.length > 0;
    $('#bxHasEvents').hidden = app.events.length === 0;

    /*
     * Итог ночи уходит в журнал ЗДЕСЬ, а не в stop(): человек размечает
     * эпизоды вердиктами уже после остановки, и каждая отметка делает
     * число точнее. saveNight обновляет запись той же ночи, а не плодит
     * дубли, поэтому звать её на каждую перерисовку безопасно.
     */
    if (app.startedAt && mins > 0) {
        saveNight({
            startedAt: app.startedAt,
            minutes: mins,
            candidates: app.events.length,
            confirmed: yes,
            rejected: no,
            sens: $('#bxSens') ? $('#bxSens').value : null,
        });
        renderNights();
    }

    for (const e of app.events) {
        const row = document.createElement('div');
        row.className = 'bx-ev' + (e.verdict ? ' v-' + e.verdict : '');

        const time = document.createElement('b');
        time.textContent = new Date(e.at).toLocaleTimeString(window.__LOCALE || 'ru',
            { hour: '2-digit', minute: '2-digit' });

        const meta = document.createElement('span');
        meta.className = 'bx-ev-meta';
        meta.textContent = fmt(T.ev_meta, {
            sec: e.duration.toFixed(1),
            score: Math.round(e.score * 100),
        });

        const actions = document.createElement('div');
        actions.className = 'bx-ev-act';

        const clip = app.clips.get(e.id);
        if (clip) {
            const play = document.createElement('button');
            play.type = 'button';
            play.className = 'tc-btn sm ghost';
            play.textContent = T.listen;
            play.addEventListener('click', () => playClip(clip, play));
            actions.appendChild(play);
        }

        for (const v of ['yes', 'no']) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tc-btn sm ' + (e.verdict === v ? '' : 'ghost');
            b.textContent = v === 'yes' ? T.mark_yes : T.mark_no;
            b.addEventListener('click', () => {
                e.verdict = e.verdict === v ? null : v;
                persist();
                renderReport();
                if (window.__aeFunnel) window.__aeFunnel('verdict', { v: e.verdict || 'clear' });
            });
            actions.appendChild(b);
        }

        row.append(time, meta, actions);
        list.appendChild(row);
    }

    renderHours();
}

/** Распределение по часам: бруксизм часто гуще во второй половине ночи. */
function renderHours() {
    const box = $('#bxHours');
    box.textContent = '';
    if (!app.events.length || !app.startedAt) { box.hidden = true; return; }
    box.hidden = false;

    const buckets = new Map();
    for (const e of app.events) {
        const h = new Date(e.at).getHours();
        buckets.set(h, (buckets.get(h) || 0) + 1);
    }
    const max = Math.max(...buckets.values());

    const from = new Date(app.startedAt).getHours();
    const to = new Date(app.endedAt || Date.now()).getHours();
    const hours = [];
    for (let h = from, i = 0; i <= 24; h = (h + 1) % 24, i++) {
        hours.push(h);
        if (h === to) break;
    }

    for (const h of hours) {
        const n = buckets.get(h) || 0;
        const col = document.createElement('div');
        col.className = 'bx-hour';
        const bar = document.createElement('i');
        bar.style.height = (n ? 8 + (n / max) * 46 : 2) + 'px';
        const lab = document.createElement('span');
        lab.textContent = String(h).padStart(2, '0');
        col.append(bar, lab);
        col.title = fmt(T.hour_title, { h: String(h).padStart(2, '0'), n });
        box.appendChild(col);
    }
}

let playing = null;
function playClip(blob, btn) {
    if (playing) { playing.pause(); playing = null; }
    const url = URL.createObjectURL(blob);
    const a = new Audio(url);
    playing = a;
    btn.disabled = true;
    a.onended = a.onerror = () => {
        btn.disabled = false;
        URL.revokeObjectURL(url);
        playing = null;
    };
    // ⚠️ Через общий шлюз: воспроизведение после await не переживает
    // пользовательскую активацию в Safari и Chrome-на-Android.
    if (window.__audioGate) window.__audioGate.play(a, url).catch(() => { btn.disabled = false; });
    else a.play().catch(() => { btn.disabled = false; });
}

async function copyReport() {
    const yes = app.events.filter(e => e.verdict === 'yes');
    const lines = [
        T.rep_head,
        fmt(T.rep_night, { date: new Date(app.startedAt).toLocaleDateString(window.__LOCALE || 'ru'), dur: fmtDur(nightMinutes()) }),
        fmt(T.rep_found, { n: app.events.length }),
        yes.length ? fmt(T.rep_yes, { n: yes.length }) : T.rep_unverified,
        T.rep_note,
    ];

    // Тенденция важнее одной ночи — если она есть, врач должен её увидеть.
    const sum = summarize(loadNights());
    if (sum && sum.nights >= 2) {
        lines.splice(2, 0, fmt(T.rep_nights, {
            nights: sum.nights,
            hours: Math.round(sum.hours),
            rate: sum.perHour.toFixed(1),
            with: sum.nightsWithEvents,
        }));
    }
    try {
        await navigator.clipboard.writeText(lines.join('\n'));
        window.__toast && window.__toast(T.copied, 'success');
    } catch (e) {
        window.__handleError(e, 'bruxism:copy');
    }
}

/*
 * Журнал ночей. Одна ночь ничего не решает — врачу несут тенденцию.
 */
function renderNights() {
    const box = $('#bxNights');
    if (!box) return;
    const nights = loadNights();
    const sum = summarize(nights);
    const wrap = $('#bxNightsBox');

    if (!sum || nights.length < 2) {
        // Одна ночь — это ещё не журнал, и показывать «среднее по одной
        // ночи» значит выдавать случайность за наблюдение.
        if (wrap) wrap.hidden = true;
        return;
    }
    if (wrap) wrap.hidden = false;
    box.textContent = '';

    const head = document.createElement('p');
    head.className = 'bxx-nsum';
    head.textContent = fmt(T.nights_summary, {
        nights: sum.nights,
        hours: Math.round(sum.hours),
        rate: sum.perHour.toFixed(1),
        with: sum.nightsWithEvents,
    });
    box.appendChild(head);

    const basis = document.createElement('p');
    basis.className = 'bxx-nbasis';
    // На чём построена тенденция — это не деталь: «шесть звуков» и
    // «шесть подтверждённых скрежетов» разные величины.
    basis.textContent = sum.basis === 'confirmed' ? T.nights_basis_confirmed : T.nights_basis_candidates;
    box.appendChild(basis);

    const bars = document.createElement('div');
    bars.className = 'bxx-nbars';
    const countOf = n => (sum.basis === 'confirmed' ? (n.confirmed | 0) : (n.candidates | 0));
    const rates = nights.map(n => (n.minutes > 0 ? countOf(n) / (n.minutes / 60) : 0));
    const top = Math.max(1, ...rates);
    nights.slice(0, 14).forEach((n, i) => {
        const cell = document.createElement('div');
        cell.className = 'bxx-nbar';
        const bar = document.createElement('i');
        bar.style.height = Math.max(3, Math.round((rates[i] / top) * 100)) + '%';
        const cap = document.createElement('span');
        const d = new Date(n.startedAt);
        cap.textContent = d.toLocaleDateString(window.__LOCALE || 'ru', { day: 'numeric', month: 'short' });
        cell.title = fmt(T.nights_bar_title, {
            date: d.toLocaleDateString(window.__LOCALE || 'ru'),
            n: countOf(n),
            dur: fmtDur(n.minutes),
        });
        cell.append(bar, cap);
        bars.appendChild(cell);
    });
    box.appendChild(bars);
}

function fmtDur(mins) {
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    return h ? fmt(T.dur_hm, { h, m }) : fmt(T.dur_m, { m });
}

function fmt(tpl, vars) {
    return String(tpl || '').replace(/:(\w+)/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
